import React, { useState } from 'react';
import { Search, Plus, Phone, IndianRupee, Hourglass } from 'lucide-react';
import { api, inr, timeAgo, STATUS_STYLES } from '../api';
import LeadModal from './LeadModal';
import { toast } from 'sonner';

const TABS = ['All', 'Open', 'In-Progress', 'High-Priority', 'Closed'];

const ARCHIVE_MS = 72 * 3600 * 1000;

const archiveCountdown = (lead) => {
  if (lead.status === 'Closed' || lead.converted || lead.archived) return null;
  const left = new Date(lead.created_at).getTime() + ARCHIVE_MS - Date.now();
  if (left > 24 * 3600 * 1000) return null;
  const hours = Math.max(0, Math.floor(left / 3600000));
  return {
    label: left <= 0 ? 'archiving…' : hours < 1 ? `${Math.max(1, Math.round(left / 60000))}m left` : `${hours}h left`,
    urgent: left < 6 * 3600 * 1000,
  };
};

export default function LeadsModule({ agent, leads, refresh, sendActivity }) {
  const [tab, setTab] = useState('All');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ customer_name: '', mobile_number: '', cart_value: '' });
  const [autoAssign, setAutoAssign] = useState(false);

  const filtered = leads.filter((l) => {
    if (tab !== 'All' && l.status !== tab) return false;
    if (search) {
      const s = search.toLowerCase();
      return l.customer_name.toLowerCase().includes(s) || l.mobile_number.includes(s) || l.order_id.toLowerCase().includes(s);
    }
    return true;
  });

  const counts = TABS.reduce((acc, t) => {
    acc[t] = t === 'All' ? leads.length : leads.filter((l) => l.status === t).length;
    return acc;
  }, {});

  const addLead = async () => {
    if (!form.customer_name || !form.mobile_number) return toast.error('Name & mobile required');
    const r = await api.post('/leads', {
      customer_name: form.customer_name,
      mobile_number: form.mobile_number,
      cart_value: Number(form.cart_value) || 0,
      assigned_to: autoAssign ? null : agent.id,
      source: 'Manual',
    });
    toast.success(autoAssign ? `Lead auto-assigned to ${r.data.assigned_to_name} (round-robin)` : 'Lead added to your board');
    sendActivity(`Added lead ${form.customer_name}`);
    setForm({ customer_name: '', mobile_number: '', cart_value: '' });
    setShowAdd(false);
    refresh();
  };

  return (
    <div data-testid="leads-pipeline-board">
      <div className="flex gap-2 mb-2.5">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-yellow-800/50" />
          <input
            data-testid="leads-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, order…"
            className="w-full bg-white/70 border border-yellow-300 rounded-full pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-yellow-400 placeholder:text-yellow-800/40"
          />
        </div>
        <button
          data-testid="add-lead-button"
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 bg-yellow-900 text-yellow-50 rounded-full px-3 py-1.5 text-xs font-bold hover:bg-yellow-800 transition-colors"
        >
          <Plus size={13} /> Lead
        </button>
      </div>

      {showAdd && (
        <div className="bg-white/80 border border-yellow-300 rounded-lg p-2.5 mb-2.5 flex flex-wrap gap-2" data-testid="add-lead-form">
          <input data-testid="add-lead-name" placeholder="Customer name" value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            className="flex-1 min-w-[120px] border border-yellow-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500" />
          <input data-testid="add-lead-mobile" placeholder="Mobile" value={form.mobile_number}
            onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
            className="w-28 border border-yellow-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500" />
          <input data-testid="add-lead-value" placeholder="Cart ₹" type="number" value={form.cart_value}
            onChange={(e) => setForm({ ...form, cart_value: e.target.value })}
            className="w-20 border border-yellow-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500" />
          <button data-testid="add-lead-submit" onClick={addLead}
            className="bg-emerald-600 text-white rounded px-3 py-1.5 text-xs font-bold hover:bg-emerald-700">Save</button>
          <label className="w-full flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none">
            <input type="checkbox" data-testid="add-lead-auto-assign" checked={autoAssign} onChange={(e) => setAutoAssign(e.target.checked)} className="accent-yellow-600" />
            Auto-assign via round-robin (instead of me)
          </label>
        </div>
      )}

      <div className="flex gap-1.5 mb-2.5 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`leads-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${tab === t ? 'bg-yellow-900 text-yellow-50 border-yellow-900' : 'bg-white/60 text-yellow-900 border-yellow-300 hover:bg-white'}`}
          >
            {t} <span className="opacity-70">{counts[t]}</span>
          </button>
        ))}
      </div>

      <div className="space-y-1.5 max-h-[380px] overflow-y-auto scroll-thin pr-1" data-testid="leads-list">
        {filtered.length === 0 && (
          <p className="font-hand text-lg text-yellow-800/60 text-center py-6">No leads here… time for chai ☕</p>
        )}
        {filtered.map((l) => {
          const cd = archiveCountdown(l);
          return (
          <button
            key={l.id}
            data-testid={`lead-row-${l.id}`}
            onClick={() => { setSelected(l); sendActivity(`Viewing lead ${l.customer_name}`); }}
            className="w-full text-left bg-white/80 hover:bg-white border border-yellow-200 rounded-lg px-3 py-2 transition-all hover:shadow-md group"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-slate-900 truncate">{l.customer_name}
                  <span className="ml-2 text-[10px] font-medium text-slate-400">{l.order_id}</span>
                </p>
                <p className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                  <span className="flex items-center gap-0.5"><Phone size={10} />{l.mobile_number}</span>
                  <span className="flex items-center gap-0.5 font-semibold text-slate-700"><IndianRupee size={10} />{Number(l.cart_value).toLocaleString('en-IN')}</span>
                  <span>{timeAgo(l.created_at)}</span>
                </p>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[l.status]}`}>
                  {l.converted ? '✓ Converted' : l.status}
                </span>
                {cd && (
                  <span
                    data-testid={`archive-countdown-${l.id}`}
                    className={`flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${cd.urgent ? 'bg-red-500 text-white animate-pulse' : 'bg-amber-100 text-amber-800 border border-amber-300'}`}
                  >
                    <Hourglass size={8} /> {cd.label}
                  </span>
                )}
              </div>
            </div>
          </button>
          );
        })}
      </div>

      {selected && (
        <LeadModal
          lead={leads.find((l) => l.id === selected.id) || selected}
          agent={agent}
          onClose={() => setSelected(null)}
          refresh={refresh}
          sendActivity={sendActivity}
        />
      )}
    </div>
  );
}
